// components/ScenarioPicker.tsx — modal for picking a scenario when starting
// a new story. Opened from StoryPicker (or any "start new" entry point).
//
// Visual language mirrors Paywall: backdrop + centred card + close X +
// ESC-to-close. Inside the card is a grid of scenario cards (one per
// /api/scenarios entry). Each card is themed with the scenario's accent
// colour from web/src/scenarios/presentation.ts so the palette is felt
// across the picker without us touching the global stylesheet.
//
// Locked cards (free user + paid scenario) are dimmed; their primary CTA
// becomes "Subscribe to play" and fires `onLockedClicked` so the parent
// can route to the Paywall.
//
// The picker does NOT collect a story name. The flow is: pick scenario →
// CharacterSetup → NamePrompt → game. ScenarioPicker.onPick takes a single
// scenarioId; the parent creates the story with a placeholder name and
// the NamePrompt step renames it via PATCH /api/stories/:id afterwards.
//
// All styles inline — additive-only constraint, no styles.ts edits.

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";
import {
  SCENARIO_PRESENTATIONS,
  type ScenarioPresentation,
} from "../scenarios/presentation";
import type { ScenarioListEntry } from "../services/scenariosApi";
import {
  SUPPORTED_LANGUAGES,
  findSupportedLanguage,
} from "../i18n/supportedLanguages";

export interface ScenarioPickerProps {
  open: boolean;
  /** Already-loaded catalogue from the parent — `locked` is server-derived. */
  scenarios: ScenarioListEntry[];
  /**
   * User chose an unlocked scenario. The optional `language` argument is
   * set ONLY when the user opened the "More options" expander and picked
   * a per-story override; otherwise it's undefined and the server uses
   * the caller's account-level language (or "en") as the default.
   */
  onPick: (scenarioId: string, language?: string) => void;
  /** User clicked a locked scenario's CTA — parent should open the Paywall. */
  onLockedClicked: () => void;
  onClose: () => void;
  /**
   * Show the "your starter world is free forever" warning above the grid.
   * Parent decides — set true iff this is a free user's first pick (no
   * stories yet AND no subscription). Default false. Under the forever-
   * default model (STORIES_CONTRACT §1) this is the one moment when ANY
   * scenario can be picked free; afterwards the user is locked to it.
   */
  firstPickWarning?: boolean;
  /**
   * Phase A i18n — the user's current account-level narration language
   * (BCP-47 code from i18n/supportedLanguages.ts, or null when never set
   * / English default). Pre-selects the language picker inside the
   * "More options" expander; "Reset to my account language" snaps back
   * to this value. Null is treated as "en".
   */
  accountLanguage?: string | null;
}

// Loose shape — `genre` is being added by a parallel agent. Don't depend
// on it; read defensively.
type LoosePresentation = ScenarioPresentation & { genre?: string };

interface ResolvedPresentation {
  pillLabel: string;
  genre: string;
  accent: string;
  accentMuted: string;
  paperEdge: string;
}

// Defaults when SCENARIO_PRESENTATIONS has no entry for an id (e.g. a
// scenario shipped server-side ahead of the web bundle). Stays inside
// the Ember palette so it doesn't visually clash.
const DEFAULT_ACCENT = "#d4a574";
const DEFAULT_ACCENT_MUTED = "rgba(212,165,116,0.45)";
const DEFAULT_PAPER_EDGE = "rgba(212,165,116,0.18)";

function resolvePresentation(scenarioId: string): ResolvedPresentation {
  const entry = SCENARIO_PRESENTATIONS[scenarioId] as
    | LoosePresentation
    | undefined;
  if (!entry) {
    return {
      pillLabel: "",
      genre: "",
      accent: DEFAULT_ACCENT,
      accentMuted: DEFAULT_ACCENT_MUTED,
      paperEdge: DEFAULT_PAPER_EDGE,
    };
  }
  return {
    pillLabel: entry.pillLabel,
    genre: typeof entry.genre === "string" ? entry.genre : "",
    accent: entry.theme?.accent ?? DEFAULT_ACCENT,
    accentMuted: entry.theme?.accentMuted ?? DEFAULT_ACCENT_MUTED,
    paperEdge: entry.theme?.paperEdge ?? DEFAULT_PAPER_EDGE,
  };
}

/** Truncate to ≤140 chars on a word boundary, single-ellipsis. */
function truncate(text: string, max = 140): string {
  if (!text || text.length <= max) return text;
  const slice = text.slice(0, max);
  const lastSpace = slice.lastIndexOf(" ");
  const trimAt = lastSpace > max * 0.6 ? lastSpace : max;
  return `${slice.slice(0, trimAt).trimEnd()}…`;
}

const STOP: (e: React.MouseEvent) => void = (e) => e.stopPropagation();

export default function ScenarioPicker({
  open,
  scenarios,
  onPick,
  onLockedClicked,
  onClose,
  firstPickWarning = false,
  accountLanguage = null,
}: ScenarioPickerProps) {
  const { t } = useTranslation(["game", "common", "scenarios"]);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Phase A i18n — "More options" expander state. Defaults closed so the
  // overwhelming majority of users never see the override; an explicit
  // open flips the expander, and any change to languageOverride is
  // applied to the next scenario pick. Null === "use my account default".
  const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);
  const accountLanguageCode = accountLanguage ?? "en";
  const [languageOverride, setLanguageOverride] = useState<string>(
    accountLanguageCode,
  );

  // Sync local override with the prop when the account preference
  // changes (e.g. the user changed it mid-session in Settings).
  useEffect(() => {
    setLanguageOverride(accountLanguageCode);
  }, [accountLanguageCode]);

  // ESC closes when open. Doing this here (not via a parent handler) so
  // the modal's lifecycle owns its own keybindings.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Focus management on open. With no name input, focus lands on the card
  // itself so keyboard-tabbing flows into the scenario cards directly.
  useEffect(() => {
    if (!open) return;
    if (cardRef.current) cardRef.current.focus();
  }, [open]);

  if (!open) return null;

  function handlePick(scenarioId: string) {
    // Only forward an explicit `language` override when the user
    // actually changed it inside the expander; otherwise leave it
    // undefined so the server picks up the account default. Treats
    // "" + the account code as "no override" so accidental open-and-
    // close of the expander doesn't bind the language.
    const override =
      languageOverride && languageOverride !== accountLanguageCode
        ? languageOverride
        : undefined;
    onPick(scenarioId, override);
  }

  return (
    <div
      style={backdropStyle}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scenario-picker-title"
    >
      <div
        ref={cardRef}
        style={cardStyle}
        onClick={STOP}
        tabIndex={-1}
      >
        <button
          type="button"
          aria-label={t("common:buttons.close")}
          style={closeBtnStyle}
          onClick={onClose}
        >
          ×
        </button>

        <div style={eyebrowStyle}>{t("game:scenarioPicker.eyebrow")}</div>
        <h2 id="scenario-picker-title" style={titleStyle}>
          {t("game:scenarioPicker.title")}
        </h2>
        <p style={bodyStyle}>{t("game:scenarioPicker.body")}</p>

        {firstPickWarning && (
          <div style={firstPickNoticeStyle}>
            <em>{t("game:scenarioPicker.firstPickWarning")}</em>
          </div>
        )}

        {scenarios.length === 0 ? (
          <div style={loadingStyle}>{t("game:scenarioPicker.loading")}</div>
        ) : (
          <>
          <div style={gridStyle}>
            {scenarios.map((s) => {
              const presentation = resolvePresentation(s.id);
              const isLocked = s.locked;
              const cardBorder = isLocked
                ? "rgba(212,165,116,0.18)"
                : presentation.paperEdge;
              const translatedGenre = t(`scenarios:${s.id}.genre`, {
                defaultValue: presentation.genre,
              });
              return (
                <article
                  key={s.id}
                  style={{
                    ...scenarioCardStyle,
                    borderColor: cardBorder,
                    opacity: isLocked ? 0.55 : 1,
                  }}
                  aria-label={`${s.name}${isLocked ? t("game:scenarioPicker.ariaLockedSuffix") : ""}`}
                >
                  <header style={scenarioCardHeaderStyle}>
                    <h3
                      style={{
                        ...scenarioCardTitleStyle,
                        color: isLocked ? "#a89070" : presentation.accent,
                      }}
                    >
                      {s.name}
                    </h3>
                    {translatedGenre && (
                      <div style={scenarioCardGenreStyle}>
                        {translatedGenre}
                      </div>
                    )}
                  </header>

                  <p style={taglineStyle}>{s.tagline}</p>
                  <p style={worldStyleStyle}>{truncate(s.worldStyle, 140)}</p>

                  <div style={metaRowStyle}>
                    <span style={metaItemStyle}>
                      <span style={metaLabelStyle}>
                        {t("game:scenarioPicker.tone")}
                      </span>
                      <span style={metaValueStyle}>{s.tone}</span>
                    </span>
                    <span style={metaItemStyle}>
                      <span style={metaLabelStyle}>
                        {t("game:scenarioPicker.rated")}
                      </span>
                      <span style={metaValueStyle}>{s.contentRating}</span>
                    </span>
                  </div>

                  {isLocked ? (
                    <button
                      type="button"
                      style={lockedCtaStyle}
                      onClick={onLockedClicked}
                    >
                      {t("game:scenarioPicker.subscribeToPlay")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      style={{
                        ...beginCtaStyle,
                        borderColor: presentation.accentMuted,
                        color: presentation.accent,
                      }}
                      onClick={() => handlePick(s.id)}
                    >
                      {t("game:scenarioPicker.begin")}
                    </button>
                  )}
                </article>
              );
            })}
          </div>

          {/* "More options" expander — collapsed by default; expands a
              per-story language override picker. The override is bound at
              creation time and never changes after that. */}
          <div style={moreOptionsWrapStyle}>
            <button
              type="button"
              style={moreOptionsToggleStyle}
              onClick={() => setMoreOptionsOpen((v) => !v)}
              aria-expanded={moreOptionsOpen}
              aria-controls="scenario-picker-more-options"
            >
              {moreOptionsOpen
                ? t("game:scenarioPicker.hideMoreOptions")
                : t("game:scenarioPicker.moreOptions")}{" "}
              {moreOptionsOpen ? "▴" : "▾"}
            </button>
            {moreOptionsOpen && (
              <div
                id="scenario-picker-more-options"
                style={moreOptionsPanelStyle}
              >
                <label style={moreOptionsLabelStyle}>
                  {t("game:scenarioPicker.languageLabel")}
                </label>
                <select
                  value={languageOverride}
                  onChange={(e) => setLanguageOverride(e.target.value)}
                  style={moreOptionsSelectStyle}
                  aria-label={t("game:scenarioPicker.languageAria")}
                >
                  {SUPPORTED_LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.nativeName}
                      {l.code !== "en" ? ` — ${l.name}` : ""}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  style={moreOptionsResetStyle}
                  onClick={() => setLanguageOverride(accountLanguageCode)}
                  disabled={languageOverride === accountLanguageCode}
                  title={t("game:scenarioPicker.resetLanguageTitle", {
                    name:
                      findSupportedLanguage(accountLanguageCode)?.nativeName ??
                      accountLanguageCode,
                  })}
                >
                  {t("game:scenarioPicker.resetLanguage")}
                </button>
                <p style={moreOptionsHintStyle}>
                  {t("game:scenarioPicker.languageHint")}
                </p>
              </div>
            )}
          </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Styles (inline — see header) ────────────────────────────────────────

const backdropStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  background: "rgba(5,3,2,0.62)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  animation: "fadein 0.25s ease-out",
};

const cardStyle: CSSProperties = {
  position: "relative",
  width: "100%",
  maxWidth: 880,
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
  padding: "40px 36px 32px",
  border: "1px solid rgba(212,165,116,0.25)",
  background:
    "linear-gradient(to bottom, rgba(28,20,14,0.96) 0%, rgba(14,10,7,0.96) 100%)",
  boxShadow: "0 0 80px rgba(0,0,0,0.7)",
  borderRadius: 2,
  color: "#e8d9b8",
  fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
  outline: "none",
};

const closeBtnStyle: CSSProperties = {
  position: "absolute",
  top: 12,
  right: 14,
  background: "transparent",
  border: "none",
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "1.2rem",
  cursor: "pointer",
  padding: 6,
  lineHeight: 1,
};

const eyebrowStyle: CSSProperties = {
  textTransform: "uppercase",
  letterSpacing: "0.18em",
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.7rem",
  textAlign: "center",
  marginBottom: 10,
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontFamily: "'IM Fell English', 'Cormorant Garamond', Georgia, serif",
  fontSize: "1.7rem",
  fontWeight: 400,
  color: "#e8c896",
  letterSpacing: "0.02em",
  textAlign: "center",
  textShadow: "0 0 24px rgba(212,165,116,0.25)",
  lineHeight: 1.2,
};

const bodyStyle: CSSProperties = {
  color: "#a89070",
  fontSize: "0.95rem",
  textAlign: "center",
  margin: "12px 0 22px",
  lineHeight: 1.55,
};

const loadingStyle: CSSProperties = {
  padding: "40px 0",
  color: "#7a6243",
  fontStyle: "italic",
  fontSize: "0.95rem",
  textAlign: "center",
};

// First-pick warning — shown only at the moment a free user is about to
// burn their forever_default_scenario_id. See firstPickWarning prop.
const firstPickNoticeStyle: CSSProperties = {
  margin: "0 0 18px",
  padding: "12px 14px 14px",
  color: "#d4b88a",
  fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif",
  fontSize: "0.95rem",
  lineHeight: 1.5,
  textAlign: "center",
  borderBottom: "1px solid rgba(212,165,116,0.18)",
};

const gridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  gap: 14,
};

const scenarioCardStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: "16px 18px",
  border: "1px solid rgba(212,165,116,0.18)",
  background: "rgba(20,14,10,0.55)",
  borderRadius: 2,
  transition: "border-color 0.18s ease-out, opacity 0.18s ease-out",
};

const scenarioCardHeaderStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const scenarioCardTitleStyle: CSSProperties = {
  margin: 0,
  fontFamily: "'IM Fell English', Georgia, serif",
  fontWeight: 400,
  fontSize: "1.25rem",
  letterSpacing: "0.01em",
  lineHeight: 1.2,
};

const scenarioCardGenreStyle: CSSProperties = {
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.66rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
};

const taglineStyle: CSSProperties = {
  margin: 0,
  fontStyle: "italic",
  color: "#c8a878",
  fontSize: "0.95rem",
  lineHeight: 1.4,
};

const worldStyleStyle: CSSProperties = {
  margin: 0,
  color: "#a89070",
  fontSize: "0.86rem",
  lineHeight: 1.55,
};

const metaRowStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: 10,
  marginTop: "auto",
  paddingTop: 6,
};

const metaItemStyle: CSSProperties = {
  display: "inline-flex",
  flexDirection: "column",
  gap: 2,
};

const metaLabelStyle: CSSProperties = {
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.62rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
};

const metaValueStyle: CSSProperties = {
  color: "#d4b88a",
  fontStyle: "italic",
  fontSize: "0.82rem",
};

const beginCtaStyle: CSSProperties = {
  marginTop: 8,
  background: "transparent",
  border: "1px solid rgba(212,165,116,0.45)",
  color: "#e8c896",
  fontFamily: "'IM Fell English', Georgia, serif",
  fontSize: "0.98rem",
  letterSpacing: "0.04em",
  padding: "9px 14px",
  cursor: "pointer",
  borderRadius: 2,
  transition: "all 0.18s ease-out",
};

const lockedCtaStyle: CSSProperties = {
  marginTop: 8,
  background: "transparent",
  border: "1px dashed rgba(212,165,116,0.35)",
  color: "#a89070",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.78rem",
  letterSpacing: "0.06em",
  padding: "9px 14px",
  cursor: "pointer",
  borderRadius: 2,
};

// ── More-options expander styles (Phase A i18n) ─────────────────────────
// Sits below the scenario grid as a subtle link → expands to a per-story
// language override. Visually subordinate to the main CTA — most users
// never engage with it.

const moreOptionsWrapStyle: CSSProperties = {
  marginTop: 22,
  paddingTop: 14,
  borderTop: "1px solid rgba(212,165,116,0.12)",
};

const moreOptionsToggleStyle: CSSProperties = {
  background: "transparent",
  border: "none",
  color: "#a89070",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.72rem",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  cursor: "pointer",
  padding: "4px 0",
};

const moreOptionsPanelStyle: CSSProperties = {
  marginTop: 12,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  alignItems: "flex-start",
};

const moreOptionsLabelStyle: CSSProperties = {
  color: "#7a6243",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.66rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
};

const moreOptionsSelectStyle: CSSProperties = {
  background: "rgba(28,20,14,0.7)",
  border: "1px solid rgba(212,165,116,0.25)",
  color: "#e8d9b8",
  fontFamily: "inherit",
  fontSize: "0.92rem",
  padding: "7px 10px",
  borderRadius: 2,
  cursor: "pointer",
  minWidth: 220,
};

const moreOptionsResetStyle: CSSProperties = {
  background: "transparent",
  border: "1px solid rgba(212,165,116,0.25)",
  color: "#a89070",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.72rem",
  letterSpacing: "0.06em",
  padding: "6px 10px",
  borderRadius: 2,
  cursor: "pointer",
};

const moreOptionsHintStyle: CSSProperties = {
  margin: 0,
  color: "#7a6243",
  fontSize: "0.82rem",
  fontStyle: "italic",
};
